import {
  forwardRef,
  memo,
  type ForwardedRef,
  type MutableRefObject,
  type Ref,
} from 'react';

import type { MenuElement } from '../Menu/types';

import { SharedActionsMenuProps } from '../../types/SharedActionsMenuProps';
import { ButtonSize } from '../../types/SharedButtonProps';
import { SharedIconProps } from '../../types/SharedIconProps';
import { SharedTooltipProps } from '../../types/SharedTooltipProps';
import { Button } from '../Button/Button';
import '../Button/Button.css';
import { IconButton } from '../Button/subcomponents/IconButton';
import { Menu } from '../Menu/Menu';
import { MenuDirection } from '../Menu/types';

function setRef<T>(ref: Ref<T> | ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  if (ref) {
    // `RefObject.current` is typed readonly; object refs are writable at runtime.
    (ref as MutableRefObject<T | null>).current = value;
  }
}

/**
 * Merge Menu's internal trigger ref with a caller-forwarded ref so this
 * component can sit inside `PopoverTrigger`. A swallowed ref leaves the
 * popover with no anchor, so it opens at (0, 0).
 */
function mergeTriggerRefs(
  menuRef: Ref<MenuElement>,
  forwardedRef: ForwardedRef<HTMLElement>,
) {
  return (element: HTMLElement | null) => {
    setRef(menuRef, element as MenuElement | null);
    setRef(forwardedRef, element);
  };
}

type ActionsMenuProps = SharedActionsMenuProps & {
  /** The trigger for the menu. */
  icon: SharedIconProps;
  /**
   * Where the menu opens relative to the trigger. Set this when the trigger
   * sits at an edge (e.g. a right-aligned row action) and the default would
   * grow the menu past that edge. @default 'bottom-start'
   */
  direction?: MenuDirection;
  /** Id for the trigger element. */
  id?: string;
  /**
   * Accessible name for the open menu. Omit to inherit the trigger's name;
   * set it when several identically named triggers each open their own menu.
   */
  label?: string;
  /**
   * Notified whenever the menu opens or closes — e.g. to keep a
   * hover-revealed trigger visible while its menu is open.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Returns focus to the trigger when the menu closes. Set `false` when
   * selecting an item moves focus elsewhere on its own (e.g. opens a new
   * field or a modal). @default true
   */
  returnFocus?: boolean;
  /**
   * Trigger size. Set this when the trigger sits alongside buttons of a size
   * the glyph ramp can't express (e.g. `xs`); otherwise it follows `icon.size`.
   */
  size?: ButtonSize;
  /**
   * Visible trigger copy. When set, the trigger renders as a labeled button
   * with the icon beside the text instead of an icon-only button. Pair with
   * `icon.position: 'right'` for a caret that trails the label.
   */
  text?: string;
  /** Trigger tooltip. Defaults to the trigger's accessible name. */
  tooltip?: SharedTooltipProps;
  /**
   * EDS-internal only — never set from src/**, only passed sibling-to-sibling
   * within packages/eds. Storybook only — mounts the menu open so static
   * frames capture it.
   */
  __private?: {
    defaultOpen?: boolean;
  };
};

export const ActionsMenu = memo(
  forwardRef<HTMLElement, ActionsMenuProps>(function ActionsMenu(
    {
      actions,
      icon,
      direction,
      groupedActions,
      id,
      label,
      onOpenChange,
      returnFocus,
      size,
      text,
      tooltip,
      __private,
    },
    forwardedRef,
  ) {
    const triggerLabel = icon.label ?? text ?? '';
    const triggerTooltip = tooltip ?? { text: icon.label };
    const triggerSize = size ?? icon.size ?? 'm';

    return (
      <Menu
        as="button"
        direction={direction}
        groupedMenuItems={groupedActions?.map((group) => ({
          title: group.title,
          items: group.actions,
        }))}
        label={label}
        menuItems={actions}
        returnFocus={returnFocus}
        onOpenChange={onOpenChange}
        trigger={({ getReferenceProps, ref }) => {
          const referenceProps = getReferenceProps();
          /* `useRole` generates an id for the trigger and points the open menu's
             `aria-labelledby` at it, so an unset `id` must not overwrite it. */
          const triggerId = id ?? (referenceProps.id as string | undefined);
          const triggerRef = mergeTriggerRefs(ref, forwardedRef);

          return text === undefined ? (
            <IconButton
              {...referenceProps}
              ref={triggerRef}
              icon={icon}
              id={triggerId}
              label={triggerLabel}
              size={triggerSize}
              tooltip={triggerTooltip}
              variant="tertiary"
            />
          ) : (
            <Button
              {...referenceProps}
              ref={triggerRef}
              icon={icon}
              id={triggerId}
              label={triggerLabel}
              size={triggerSize}
              text={text}
              tooltip={triggerTooltip}
              variant="tertiary"
            />
          );
        }}
        __private={__private}
      />
    );
  }),
);

ActionsMenu.displayName = 'ActionsMenu';
